<template>
  <USlideover
    :open="modelValue"
    :title="title"
    :description="description"
    :side="placement"
    :content="contentProps"
    :ui="ui"
    @update:open="onOpenChange"
    @after:leave="onAfterLeave"
  >
    <template
      v-if="$slots['title']"
      #title
    >
      <slot name="title" />
    </template>

    <template
      v-if="$slots['content']"
      #content
    >
      <slot name="content" />
    </template>

    <template
      v-else
      #body
    >
      <slot />
    </template>

    <template
      v-if="$slots['footer']"
      #footer
    >
      <slot name="footer" />
    </template>
  </USlideover>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import USlideover from '@nuxt/ui/components/Slideover.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useOverlayShell } from '~/composables/useOverlayShell'
import type { OverlayShellEvents } from '~/types/overlays/OverlayShellEvents'

type DrawerSide = 'left' | 'right'

const props = withDefaults(defineProps<Props>(), {
  blurBackdrop: true,
  closeOnOutsideClick: true,
  description: '',
  maxWidthClass: 'max-w-3xl',
  side: 'right'
})

const emit = defineEmits<OverlayShellEvents>()

interface Props {
  /** Controls whether the drawer is open. */
  modelValue: boolean
  /** Drawer heading text. */
  title: string
  /** Optional supporting text below the heading. */
  description?: string
  /** Tailwind classes controlling the drawer max width. */
  maxWidthClass?: string
  /** Allows closing when the backdrop is clicked. */
  closeOnOutsideClick?: boolean
  /** Applies a blur effect to the backdrop. */
  blurBackdrop?: boolean
  /** Expands the drawer to the viewport width on mobile. */
  fullScreenOnMobile?: boolean
  /** Renders a bottom sheet instead of a side drawer on mobile. */
  bottomSheetOnMobile?: boolean
  /** The viewport edge the drawer opens from. */
  side?: DrawerSide
}

defineSlots<{
  default?: () => unknown
  footer?: () => unknown
  /** Replaces the whole panel, for a drawer that brings its own layout. */
  content?: () => unknown
  /** Replaces the heading text, for a drawer headed by something else. */
  title?: () => unknown
}>()

const isMobileViewport = useMediaQuery('(max-width: 767px)')

// One declaration serves both placements: `side` moves the panel, so the
// header, body and footer are written once.
const placement = computed(() =>
  isMobileViewport.value && props.bottomSheetOnMobile ? 'bottom' : props.side
)

const widthClass = computed(() => {
  if (props.fullScreenOnMobile) return 'w-full'
  return placement.value === 'bottom'
    ? 'w-[calc(100%-2rem)]'
    : 'w-[calc(100%-1rem)] sm:w-[calc(100%-2rem)]'
})

const ui = computed(() => ({
  content: [
    widthClass.value,
    'max-w-none',
    props.maxWidthClass,
    placement.value === 'bottom' ? 'max-h-[80vh] mx-auto rounded-t-2xl' : ''
  ],
  overlay: props.blurBackdrop ? 'backdrop-blur-[1px] bg-surface-level-950/55' : 'bg-surface-clear'
}))

const { onOpenChange, onAfterLeave, contentProps } = useOverlayShell({
  closeOnOutsideClick: () => props.closeOnOutsideClick,
  emit,
  isOpen: () => props.modelValue
})
</script>
